import {
  ArrowRightIcon,
  FieldErrorAndDescription,
  HatWithCheckIcon,
  HatWithPlusIcon,
} from '@systemeio/ui'
import { AccessTypeEnum } from 'modules/courses/courses/curriculum/enums/access-type-enum'
import { useCourse } from 'modules/courses/courses/hooks/use-course'
import { LightCourseEditor } from 'modules/courses/courses/light-course/components/light-course-editor'
import { CourseEditorTypeEnum, CourseId } from 'modules/courses/courses/types/course-interface'
import { DigitalResourcesTypeEnum } from 'modules/digital-product/enums/digital-resources-type-enum'
import { DigitalProductChangeInterface } from 'modules/digital-product/types/digital-product-interface'
import {
  ProductECourseItemEditType,
  ProductECourseItemErrorsType,
} from 'modules/stores/store/content/product/types/product-interface'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'
import { AdvancedCourse } from './advanced-course'
import { CourseTypeEnum } from './enum/course-type'
import { useCoursePreview } from './hooks/use-course-preview'
import { useCourseCurriculum } from 'modules/courses/courses/curriculum/hooks/use-cource-curriculum'

type Props = {
  courseId?: CourseId
  tempState: ProductECourseItemEditType
  tempProduct: DigitalProductChangeInterface
  errors: ProductECourseItemErrorsType
  setTempState: Dispatch<SetStateAction<ProductECourseItemEditType>>
  setTempProduct: Dispatch<SetStateAction<DigitalProductChangeInterface>>
  onToggleLectureEditor?: (lectureId?: number) => void
}

export const CourseGateway = ({
  courseId,
  tempState,
  tempProduct,
  errors,
  setTempState,
  setTempProduct,
  onToggleLectureEditor,
}: Props) => {
  const { t } = useLocoTranslation()
  const [courseType, setCourseType] = useState<CourseTypeEnum | null>(null)

  const { storeId } = useStoreQuery()
  const { data: store } = useStore(storeId)
  const { courseData } = useCourse(courseId)

  const { data: courseCurriculumData } = useCourseCurriculum(courseId)

  const { showCoursePreview } = useCoursePreview()

  const isLightCourse = courseData?.courseType === CourseEditorTypeEnum.LightCourse
  const isAdvancedCourse = courseData?.courseType === CourseEditorTypeEnum.ClassicCourse

  const handleBackToGateway = () => {
    setCourseType(null)
  }

  const handleLightCourseSelect = async () => {
    setCourseType(CourseTypeEnum.Light)
  }

  const handleAdvancedCourseSelect = () => {
    setCourseType(CourseTypeEnum.Advanced)
  }

  const handleCourseChange = (courseId?: number) => {
    setTempState(prev => ({ ...prev, courseId }))

    setTempProduct(prev => ({
      ...prev,
      resources: [
        {
          course: courseId,
          enrollmentAccessType: AccessTypeEnum.fullAccess,
          modules: [],
          type: DigitalResourcesTypeEnum.membership_course,
        },
      ],
    }))
  }

  const handleToggleLectureEditor = useCallback(
    (lectureId?: number) => {
      if (!lectureId) {
        showCoursePreview(courseData, courseCurriculumData)
      }

      onToggleLectureEditor?.(lectureId)
    },
    [courseData, courseCurriculumData, showCoursePreview, onToggleLectureEditor],
  )

  useEffect(() => {
    showCoursePreview(courseData, courseCurriculumData)
  }, [courseData, courseCurriculumData, showCoursePreview])

  const TILE_CLASSNAME =
    'relative w-full md:w-[446px] p-4 md:p-5 pb-8 md:pb-10 flex flex-col items-start gap-4 border rounded-lg border-text-tertiary/50 hover:border-brand-primary hover:border cursor-pointer main-transition-colors'

  const backButton = (
    <button
      onClick={handleBackToGateway}
      className="hover:text-text-primary main-transition-colors group flex items-center gap-2 text-lg font-medium"
    >
      <div className="[&>svg>path]:fill-text-tertiary group-hover:[&>svg>path]:fill-text-primary [&>svg>path]:main-transition-colors rotate-180">
        <ArrowRightIcon />
      </div>
      {t('dashboard.store.course_type_selection')}
    </button>
  )

  if (!courseType)
    return (
      <div className="flex flex-col gap-5">
        <div className="flex flex-col items-stretch gap-4 md:flex-row xl:gap-8">
          <div
            className={twMerge(TILE_CLASSNAME, isLightCourse && 'border-brand-primary border')}
            onClick={handleLightCourseSelect}
          >
            <span className="bg-brand-primary/20 text-text-primary absolute right-4 top-4 rounded-3xl px-2 py-1 text-xs font-semibold md:right-5 md:top-5 md:px-3 md:py-1 md:text-sm">
              {t('dashboard.light_course.recommended')}
            </span>

            <HatWithPlusIcon
              className={twMerge(
                '[&>svg>path]:main-transition-colors',
                isLightCourse && 'fill-brand-primary [&>path]:fill-brand-primary',
              )}
            />

            <div className="text-text-primary text-lg font-extrabold">
              {t('dashboard.light_course.title')}
            </div>

            <p className="text-sm font-normal">{t('dashboard.light_course.description')}</p>

            {isLightCourse && (
              <div className="text-text-primary mt-auto text-base font-bold">
                <span className="mr-1">{t('dashboard.store.selected_course')}:</span>
                <span>{courseData.name}</span>
              </div>
            )}
          </div>

          <div
            className={twMerge(TILE_CLASSNAME, isAdvancedCourse && 'border-brand-primary border')}
            onClick={handleAdvancedCourseSelect}
          >
            <HatWithCheckIcon
              className={twMerge(
                '[&>svg>path]:main-transition-colors',
                isAdvancedCourse && 'fill-brand-primary [&>path]:fill-brand-primary',
              )}
            />

            <div className="text-text-primary text-lg font-extrabold">
              {t('dashboard.store.advanced_course.title')}
            </div>

            <p className="text-sm font-normal">
              {t('dashboard.store.advanced_course.description')}
            </p>

            {isAdvancedCourse && (
              <div className="text-text-primary mt-auto text-base font-bold">
                <span className="mr-1">{t('dashboard.store.selected_course')}:</span>
                <span>{courseData.name}</span>
              </div>
            )}
          </div>
        </div>

        <FieldErrorAndDescription
          error={
            errors['courseId'] &&
            t('dashboard.store.item.e_course.select_course.course_is_not_selected_error')
          }
        />
      </div>
    )

  if (courseType === CourseTypeEnum.Light)
    return (
      <LightCourseEditor
        domainId={store?.domainId}
        courseId={tempState.courseId ?? undefined}
        courseSelectorError={errors['courseId']}
        backButton={backButton}
        onChangeCourse={handleCourseChange}
        onToggleLectureEditor={handleToggleLectureEditor}
      />
    )

  if (courseType === CourseTypeEnum.Advanced)
    return (
      <div className="flex flex-col gap-5">
        {backButton}

        <AdvancedCourse
          courseId={tempState.courseId ?? undefined}
          courseSelectorError={errors['courseId']}
          onChangeCourse={handleCourseChange}
        />
      </div>
    )

  return null
}
